import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeyValueType, KeyValueUserModel } from '@models/common';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoCheckboxControl, CipoFieldConfig, CipoListControl } from '@shared/components/fields/common';
import { DictionaryStore } from '@signal-store/index';
import { NotificationFormData, NotificationFormValue } from '../common';

@Component({
  selector: 'cipo-notification-form',
  imports: [CipoSelectComponent, CipoCheckboxComponent, FlexLayoutModule],
  templateUrl: './notification-form.component.html',
})
export class NotificationFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dictionaryStore = inject(DictionaryStore);
  private destroyRef = inject(DestroyRef);
  disabled = input<boolean>(false);
  compactMode = input<boolean>(false);
  data = input.required<NotificationFormData>();
  valueChanged = output<{
    notify?: string[];
    excludeRoles?: number[];
    includeAttachments?: boolean;
  }>();

  form = this.fb.group({
    notify: new CipoListControl([], {
      label: 'roles.notify',
      multiple: true,
      allSelectedLabel: 'general.allSelected',
      toggleAllOutside: true,
    }),
    excludeRoles: new CipoListControl([], {
      label: 'roles.excludeRoles',
      multiple: true,
      allSelectedLabel: 'general.allSelected',
      toggleAllOutside: true,
    }),
    includeAttachments: new CipoCheckboxControl(false, { label: 'transition.includeAttachments' }),
  });
  excludeRolesOptions = signal<KeyValueType<number>[]>([]);
  private previousNotifySelection = signal<string[]>([]);

  originalFieldsData = computed<Record<'notify' | 'excludeRoles' | 'includeAttachments', CipoFieldConfig>>(() => ({
    notify: {
      label: 'roles.notify',
      multiple: true,
      allSelectedLabel: 'general.allSelected',
      toggleAllOutside: true,
      options: this.data().notifyOptions ?? [],
    },
    excludeRoles: {
      label: 'roles.excludeRoles',
      multiple: true,
      allSelectedLabel: 'general.allSelected',
      toggleAllOutside: true,
      options: this.excludeRolesOptions(),
    },
    includeAttachments: {
      label: 'transition.includeAttachments',
    },
  }));

  mappedFieldsData = linkedSignal(() => {
    const isDisabled = this.disabled();
    const fieldsData = this.originalFieldsData();
    return {
      notify: { ...fieldsData.notify, readonly: isDisabled },
      excludeRoles: { ...fieldsData.excludeRoles, readonly: isDisabled },
      includeAttachments: { ...fieldsData.includeAttachments, readonly: isDisabled },
    };
  });

  constructor() {
    effect(() => {
      const { notify, excludeRoles, includeAttachments } = this.data();
      this.form.patchValue({ notify, excludeRoles, includeAttachments });
      this.previousNotifySelection.set([...(notify ?? [])]);
    });

    effect(() => {
      const isDisabled = this.disabled();
      if (isDisabled) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit() {
    const { contractId, moduleId } = this.data();
    this.dictionaryStore
      .getModuleRoles({ contractId, moduleId })
      .subscribe(roles => this.excludeRolesOptions.set(roles));

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncNotifySelectionWithGroups();
      this.valueChanged.emit(this.form.getRawValue() as NotificationFormValue);
    });
  }

  private syncNotifySelectionWithGroups() {
    const currentNotify = this.form.controls.notify.value ?? [];
    const previousNotify = this.previousNotifySelection();
    const options = this.data().notifyOptions ?? [];

    if (!currentNotify.length && !previousNotify.length) {
      return;
    }

    const selectedSet = new Set(currentNotify);
    const previousSet = new Set(previousNotify);

    const groups = options.filter(option => option.isGroup);
    const users = options.filter(option => !option.isGroup);

    const usersByRole = users.reduce<Record<number, KeyValueUserModel<string>[]>>((acc, user) => {
      (user.roleIds ?? []).forEach(roleId => {
        if (!acc[roleId]) {
          acc[roleId] = [];
        }
        acc[roleId].push(user);
      });
      return acc;
    }, {});

    const deselectedGroups = groups.filter(group => previousSet.has(group.key) && !selectedSet.has(group.key));
    const deselectedUsers = users.filter(user => previousSet.has(user.key) && !selectedSet.has(user.key));

    // If a user is manually deselected, remove its selected group(s) first.
    // This prevents the selected group from adding that user back immediately.
    deselectedUsers.forEach(user => {
      (user.roleIds ?? []).forEach(roleId => {
        const groupKey = String(roleId);
        if (selectedSet.has(groupKey)) {
          selectedSet.delete(groupKey);
        }
      });
    });

    deselectedGroups.forEach(group => {
      const roleUsers = usersByRole[Number(group.key)] ?? [];
      roleUsers.forEach(user => selectedSet.delete(user.key));
    });

    const selectedGroups = groups.filter(group => selectedSet.has(group.key));
    selectedGroups.forEach(group => {
      const roleUsers = usersByRole[Number(group.key)] ?? [];
      roleUsers.forEach(user => selectedSet.add(user.key));
    });

    const synchronizedNotify = [...selectedSet] as string[];
    const changed =
      synchronizedNotify.length !== currentNotify.length ||
      synchronizedNotify.some(key => !currentNotify.includes(key));

    this.previousNotifySelection.set([...synchronizedNotify]);
    if (changed) {
      this.form.controls.notify.patchValue(synchronizedNotify);
    }
  }
}
